import React, { createContext, useState, useContext, useCallback, useRef } from 'react';

const CallContext = createContext();

/**
 * CallProvider — global active call state management.
 *
 * Tracks active call state, mute status (synced from Jitsi API),
 * call timer, participant info, and forwards commands to Jitsi.
 * Components check `activeCall` to switch between auto/manual mode for Vedic tools.
 */
export const CallProvider = ({ children }) => {
  const [activeCall, setActiveCall] = useState(null);
  // activeCall shape:
  // { consultationId, callType, userProfile, isExpanded,
  //   isAudioMuted, isVideoMuted, callStartTime, participantName,
  //   ratePerMin, totalCharged, astrologerEarning }

  const jitsiApiRef = useRef(null);

  const startCall = useCallback((consultationId, callType, { userProfile, chatRoomId, displayName, ratePerMin } = {}) => {
    setActiveCall({
      consultationId,
      callType,
      userProfile: userProfile || null,
      chatRoomId: chatRoomId || `instant_${consultationId}`,
      displayName: displayName || 'User',
      isExpanded: false,
      isAudioMuted: false,
      isVideoMuted: callType === 'call',
      callStartTime: null,
      participantName: '',
      ratePerMin: ratePerMin || 0,
      totalCharged: 0,
      astrologerEarning: 0,
    });
  }, []);

  const endCall = useCallback(() => {
    jitsiApiRef.current = null;
    setActiveCall(null);
  }, []);

  const setExpanded = useCallback((expanded) => {
    setActiveCall((prev) => prev ? { ...prev, isExpanded: expanded } : null);
  }, []);

  const setUserProfile = useCallback((profile) => {
    setActiveCall((prev) => prev ? { ...prev, userProfile: profile } : null);
  }, []);

  const updateCallType = useCallback((callType) => {
    setActiveCall((prev) => prev ? { ...prev, callType } : null);
  }, []);

  // Jitsi API state sync — called by JitsiCall when events fire
  const setAudioMuted = useCallback((muted) => {
    setActiveCall((prev) => prev ? { ...prev, isAudioMuted: muted } : null);
  }, []);

  const setVideoMuted = useCallback((muted) => {
    setActiveCall((prev) => prev ? { ...prev, isVideoMuted: muted } : null);
  }, []);

  const setCallStartTime = useCallback((timestamp) => {
    setActiveCall((prev) => prev ? { ...prev, callStartTime: timestamp } : null);
  }, []);

  const setParticipantName = useCallback((name) => {
    setActiveCall((prev) => prev ? { ...prev, participantName: name } : null);
  }, []);

  // Billing data updates — called by billing timer or socket events
  const updateBilling = useCallback(({ totalCharged, astrologerEarning, ratePerMin } = {}) => {
    setActiveCall((prev) => {
      if (!prev) return null;
      const updates = {};
      if (totalCharged !== undefined) updates.totalCharged = totalCharged;
      if (astrologerEarning !== undefined) updates.astrologerEarning = astrologerEarning;
      if (ratePerMin !== undefined) updates.ratePerMin = ratePerMin;
      return { ...prev, ...updates };
    });
  }, []);

  // Store Jitsi API ref for command forwarding
  const setJitsiApi = useCallback((api) => {
    jitsiApiRef.current = api;
  }, []);

  // Check if a Jitsi API instance already exists (defense-in-depth for #343)
  const hasJitsiApi = useCallback(() => {
    return !!jitsiApiRef.current;
  }, []);

  // Command forwarding — any component can call these
  const toggleAudio = useCallback(() => {
    if (jitsiApiRef.current) jitsiApiRef.current.executeCommand('toggleAudio');
  }, []);

  const toggleVideo = useCallback(() => {
    if (jitsiApiRef.current) jitsiApiRef.current.executeCommand('toggleVideo');
  }, []);

  const hangup = useCallback(() => {
    if (jitsiApiRef.current) jitsiApiRef.current.executeCommand('hangup');
  }, []);

  const value = {
    activeCall,
    startCall,
    endCall,
    setExpanded,
    setUserProfile,
    updateCallType,
    hasActiveCall: !!activeCall,
    // Jitsi state sync
    setAudioMuted,
    setVideoMuted,
    setCallStartTime,
    setParticipantName,
    setJitsiApi,
    // Billing
    updateBilling,
    hasJitsiApi,
    // Command forwarding
    toggleAudio,
    toggleVideo,
    hangup,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
};

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
};
